import moment from "moment";

import { useEffect, useRef, useState } from "react";
import { Form, InputGroup } from "react-bootstrap";
import InputGroupText from "react-bootstrap/esm/InputGroupText";

import { overviewData, projectsList } from "../../../shared/settings/apiConfig";
import { PROJECT_METRICS } from "../../../shared/utils/constants";
import { formatCurrency } from "../../../shared/utils/formatCurrency";
import { Datatable } from "../../../shared/datatable/Datatable";
import { BuildingIcon, DateIcon, FilterIcon, GridIcon, ListIcon, PlusIcon, RupeeIcon } from "../../../shared/icons/Icons";
import { DropdownInput } from "../../../shared/form/FormInput";
import { getApi } from "../../../shared/services/apiHelper";

import './ProjectList.scss';

const statusDropdown = [
    { id: "All", value: "All Status" },
    { id: "InProgress", value: "In Progress" },
    { id: "Completed", value: "Completed" },
    { id: "Failed", value: "Failed" },
    { id: "Estimated", value: "Estimated" }
];

export default function ProjectList (props) {

    const customCost = (row) => (
        <div className="d-flex align-items-center text-gradient">
            <RupeeIcon color="15" />
            {row.cost || '0'}
        </div>
    );

    const customDate = (dataValue) => {
        if (!dataValue) return '- - -';
        
        const date = moment.utc(dataValue).local();        
        return (
            <div className="d-flex align-items-center gap-3">
                <DateIcon />
                {date.format('YYYY-MM-DD')}
            </div>
        )
    };

    const customName = (row) => {
        return (
            <div className="d-flex align-items-center gap-2">
                <div className="total-icon rounded p-2"><BuildingIcon color="#FFF" /></div>
                <div className="text-m font-weight-bold">{row.name}</div>
            </div>
        );
    }

    const customStatus = (status) => (
        <div className={`status-badge ${status?.toLowerCase() === 'completed' ? 'status-badge-green' : ''}`}>
            {status || '- - -'}
        </div>
    );

    const dataTable = useRef(null);
    const [search, setSearch] = useState("");
    const [metrics, setMetrics] = useState(PROJECT_METRICS);
    const [activeDomain, setActiveDomain] = useState("Grid View");
    const [status, setStatus] = useState("");

    const [config] = useState({
        name: 'projects',
        url: projectsList,
        columns: [
            { name: 'Project Name', sort: false, width: '30%', customColumn: customName },
            { name: 'Status', property: 'status', sort: false, width: '20%', customColumn: customStatus },
            { name: 'Created', property: 'created_at', sort: false, width: '20%', customColumn: customDate },
            { name: 'Estimated Cost', sort: false, width: '20%', customColumn: customCost },
            { name: 'Actions', sort: false, width: '10%', actions: { row: [{ type: 'commonAction' }] } },
        ],
        field: [],
        filter: [{ "field": "is_deleted", "value": false, "op": "==", "join": "AND" }],
        sort: [{ "field": "created_at", "direction": "desc" }],
        pagination: true,
        editLink: "/projects/edit/"
    });

    useEffect(() => {
        getApi(overviewData).then(response => {
            const metricsData = response.data;
            setMetrics(prevState => {
                return prevState.map(metric => {
                    const key = metric.key;
                    const value = metricsData[key];
                    return { ...metric, value: value !== undefined ? value : metric.value };
                });
            });
        }).catch(error => {
            console.log("Failed to fetch project metrics:", error);
        });
    }, []);

    useEffect(() => {
        let filters = [];

        if (search?.trim()) {
            filters.push(
                { field: 'name', value: search, op: 'LIKE', join: 'AND' }
            );
        }

        if (status && status !== "All" && status !== "") {
            filters.push({ field: 'status', value: status, op: '==', join: 'AND' });
        }

        if (filters.length > 0 && dataTable.current) {
            dataTable.current.searchData(filters);
        } else if (dataTable.current) {
            clearData();
        }
    }, [search, status]);

    const clearData = () => {
        dataTable.current?.searchData([]);
    }

    const navigateToCreateProject = () => {
        props.history.push("/projects/select-type");
    }

    return(
        <div className="assignment-list main-content animate-fade-in">
            <div className="d-flex justify-content-between align-items-center">
                <div>
                    <h3 className="content-label">Projects</h3>
                    <p className='content-sub-label'>Manage all your construction projects</p>
                </div>
                <div>
                   {/* <Button className="btn-solid-blue fw-bold" onClick={() => navigateToCreateProject()}><PlusIcon /> New Project</Button> */}
                   <div className="btn btn-solid-blue fw-bold" onClick={() => navigateToCreateProject()}><PlusIcon /> New Project</div>
                </div>
            </div>

            <div className="metrics-row">
                {
                    metrics.map((elem, index) => (
                        <div className="metric-card-view d-flex align-items-center flex-grow-1 gap-2" key={"quick-links-" + index}>
                            <div className={`p-2 rounded ${elem.className}`}>
                                <elem.icon color={"#FFF"}/>
                            </div>
                            <div>
                                <div className="fw-bold text-gray text-n">{elem.label}</div>
                                <div className="font-weight-bold text-2l">
                                    {elem.key === "totalestimatedvalue" ? formatCurrency(elem.value) : elem.value}
                                </div>
                            </div>
                        </div>
                    ))
                }
            </div>
            
            <div className="activity-card user-input-section d-flex p-3 gap-3">
                <InputGroup className="p-0 rounded">
                    <InputGroupText className="search" style={{ position: 'static' }}>
                        <i className="fa fa-search ml-3" ></i>
                    </InputGroupText>

                    <span className="icon-envelope-o"></span>
                    <Form.Control className={`search`} type={'text'} style={{ borderRadius: '0px 5px 5px 0px', borderLeft: 'none' }} name={"search"} placeholder={"Search projects..."}
                        onChange={(event) => { setSearch(event.target.value) }} value={search} />
                </InputGroup>

                <div className="filter-dropdown-wrapper d-flex align-items-center rounded gap-3" style={{ width: "20%" }}>
                    <DropdownInput icon={<FilterIcon size={15} />} name='All Status' defaultValue="All Status" value={status} options={statusDropdown} onChange={(value) => { setStatus(value) } } />
                </div>

                <div className="view-toggle d-flex align-items-center border rounded clickable">
                    <span className={`p-2 ${activeDomain === "Grid View" ? "text-purple" : ""}`} onClick={() => setActiveDomain("Grid View")}><GridIcon color={`${activeDomain === "Grid View" ? "#7e22ce" : "#6b7280" }`} /></span>
                    <span className={`p-2 ${activeDomain === "List View" ? "text-purple" : ""}`} onClick={() => setActiveDomain("List View")}><ListIcon color={`${activeDomain === "List View" ? "#7e22ce" : "#6b7280" }`} /></span>
                </div>
            </div>

            <div className="mt-3">
                <Datatable ref={dataTable} config={config} viewType={activeDomain === 'Grid View' ? 'grid' : 'list'} props={props} history={props.history} onDelete={(row) => {
                    if (dataTable.current) {
                        dataTable.current.navigateToDelete(row);
                    }
                }} />
            </div>
        </div>
    )
}